import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../components/Button'
import axios from 'axios'
import { API_BASE_URL } from '../config'
import { useAuth } from '../contexts/AuthContext'

const HistoryPage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [selectedDateRange, setSelectedDateRange] = useState('all')
  const [conversationHistory, setConversationHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const cleanContent = (text) => {
    if (!text) return text;
    // Strip tag markers but keep the content inside
    return text
      .replace(/<[^>]*>/g, '')
      .replace(/&lt;.*?&gt;/g, '')
      .trim();
  }

  useEffect(() => {
    if (isAuthenticated) {
      fetchHistory()
    }
  }, [isAuthenticated])

  const fetchHistory = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(`${API_BASE_URL}/api/agent/sessions/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })

      setConversationHistory(response.data.map(session => {
        const cleaned = cleanContent(session.last_message?.content);

        return {
          id: session.id,
          title: cleaned || "New Conversation",
          date: session.updated_at,
          messageCount: session.message_count,
          preview: cleaned || "No messages yet",
          lastMessageRole: session.last_message?.role
        }
      }))
    } catch (error) {
      console.error('Failed to fetch conversation history:', error)
      setConversationHistory([])
    } finally {
      setIsLoading(false)
    }
  }

  const filters = [
    { id: 'all', label: 'All Conversations', icon: '📋' },
    { id: 'chat', label: 'AI Chat', icon: '💬' },
    { id: 'chart', label: 'Technical Chart', icon: '📈' },
    { id: 'screener', label: 'Stock Screener', icon: '🔍' },
    { id: 'flow', label: 'Flow Builder', icon: '🔄' }
  ]

  const dateRanges = [
    { id: 'all', label: 'All Time' },
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'quarter', label: 'This Quarter' }
  ]

  const filteredConversations = () => {
    let filtered = conversationHistory

    if (searchQuery) {
      filtered = filtered.filter(conv =>
        conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        conv.preview.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date))
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

    if (diffInHours < 24) {
      return `${diffInHours} hours ago`
    } else if (diffInHours < 48) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString()
    }
  }

  const getPageIcon = (page) => {
    const iconMap = {
      'AI Chat': '💬',
      'Technical Chart': '📈',
      'Stock Screener': '🔍',
      'Flow Builder': '🔄'
    }
    return iconMap[page] || '📄'
  }

  if (isLoading) {
    return (
      <div className="flex h-full bg-gray-50 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading conversation history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full bg-white overflow-hidden">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-3">Conversation History</h1>
              <p className="text-lg text-gray-500">Pick up where you left off with TraderBro AI.</p>
            </div>

            {/* Large Search Section */}
            <div className="relative mb-12 group">
              <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none">
                <span className="text-2xl text-gray-400 group-focus-within:text-blue-500 transition-colors">🔍</span>
              </div>
              <input
                type="text"
                placeholder="Search conversations by content or topic..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-16 pr-6 py-5 bg-gray-50 border-2 border-transparent rounded-2xl text-lg font-medium placeholder-gray-400 focus:bg-white focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-50 transition-all shadow-sm"
              />
            </div>

            {/* Result Header */}
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
              <div className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                {filteredConversations().length === 0 ? 'No matches' : `${filteredConversations().length} Conversations`}
              </div>
            </div>

            {/* Simplified List */}
            <div className="space-y-4">
              {filteredConversations().length === 0 ? (
                <div className="text-center py-24 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-100">
                  <div className="text-6xl mb-6 opacity-30">📚</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Nothing found</h3>
                  <p className="text-gray-500 max-w-xs mx-auto">Try searching for different keywords or start a new chat.</p>
                </div>
              ) : (
                filteredConversations().map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => navigate(`/chat?thread=${conversation.id}`)}
                    className="w-full text-left bg-white rounded-2xl border border-gray-100 p-6 hover:border-blue-200 hover:shadow-xl transition-all group relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 w-1.5 h-full bg-transparent group-hover:bg-blue-500 transition-colors"></div>
                    <div className="flex items-start gap-5">
                      <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-2xl group-hover:bg-blue-50 transition-colors">
                        💬
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate pr-4 text-lg">
                            {conversation.title}
                          </h3>
                          <span className="text-xs font-bold text-gray-400 whitespace-nowrap bg-gray-50 px-2 py-1 rounded-md">
                            {formatDate(conversation.date)}
                          </span>
                        </div>
                        <p className="text-gray-500 line-clamp-2 text-sm leading-relaxed mb-4">
                          {conversation.preview}
                        </p>
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-gray-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                            AI Chat
                          </span>
                          <span className="text-xs font-bold text-gray-300">•</span>
                          <span className="text-xs font-bold text-gray-400">
                            {conversation.messageCount} messages
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoryPage 